import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import axios from 'axios';
import { clearChatStorage } from '../../../services/ai-explorer/chatStorage';
import './ImpersonationBanner.css';

const SUPERADMIN_API_URL =
  (typeof window !== 'undefined' && window.env?.REACT_APP_SUPERADMIN_API_URL) ||
  process.env.REACT_APP_SUPERADMIN_API_URL ||
  'http://localhost:8003';

function decodeJwt(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded + '==='.slice((padded.length + 3) % 4)));
  } catch {
    return null;
  }
}

function readImpersonationClaims() {
  const claims = decodeJwt(localStorage.getItem('jwt_token'));
  if (!claims || !claims.imp_super_admin_id) return null;
  return {
    superAdminEmail: claims.imp_super_admin_email,
    sessionId: claims.imp_session_id,
    expiresAt: claims.exp ? claims.exp * 1000 : null,
  };
}

function readTargetEmail() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return u?.email || null;
  } catch {
    return null;
  }
}

function formatCountdown(ms) {
  if (ms == null || ms <= 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const ImpersonationBanner = () => {
  const { t } = useTranslation('commonUi');

  const [claims] = useState(() => readImpersonationClaims());
  const [now, setNow] = useState(() => Date.now());
  const [ending, setEnding] = useState(false);
  const targetEmail = useMemo(readTargetEmail, [claims]);

  // Tick every second so the countdown updates. Cheap because the banner
  // only mounts when impersonating.
  useEffect(() => {
    if (!claims) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [claims]);

  // Auto-clear when the JWT expires — otherwise the user sees the banner
  // claim "0:00" while the next API call 401s anyway.
  useEffect(() => {
    if (!claims?.expiresAt) return;
    if (now >= claims.expiresAt) {
      handleEnd('session_expired', { skipBackend: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, claims]);

  const handleEnd = async (reason = 'ended_by_target_user', { skipBackend = false } = {}) => {
    if (ending) return;
    setEnding(true);

    if (!skipBackend && claims?.sessionId) {
      try {
        await axios.post(
          `${SUPERADMIN_API_URL.replace(/\/+$/, '')}/api/v1/impersonate/end-public`,
          { session_id: claims.sessionId, reason },
        );
      } catch (e) {
        // Best-effort. The session also auto-ends server-side at TTL,
        // and the token is being thrown away locally regardless.
        // eslint-disable-next-line no-console
        console.warn('Failed to end impersonation server-side:', e);
      }
    }

    localStorage.removeItem('jwt_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    // The impersonated user's Saf3AI Agent chat must not carry over.
    clearChatStorage();
    // Hard nav so every in-memory module-scoped cache (entitlements,
    // axios interceptors closing over old tokens, etc.) gets reset.
    window.location.assign('/');
  };

  if (!claims) return null;
  const remaining = claims.expiresAt ? claims.expiresAt - now : null;
  const expiringSoon = remaining != null && remaining < 2 * 60 * 1000;

  return (
    <div className={`impersonation-banner ${expiringSoon ? 'expiring' : ''}`} role="alert">
      <div className="impersonation-banner__inner">
        <span className="impersonation-banner__dot" aria-hidden="true" />
        <span className="impersonation-banner__text">
          <Trans i18nKey="impersonationbannerImpersonationbanner.viewingAs" ns="commonUi"
            values={{ target: targetEmail || t('commonUi:impersonationbannerImpersonationbanner.targetUserFallback'),
                      admin: claims.superAdminEmail || t('commonUi:impersonationbannerImpersonationbanner.adminFallback') }}
            components={[<strong />, <strong />]} />
          {remaining != null && <> {t('commonUi:impersonationbannerImpersonationbanner.expiresIn')} <strong>{formatCountdown(remaining)}</strong></>}.
        </span>
        <button
          type="button"
          className="impersonation-banner__end"
          onClick={() => handleEnd('ended_by_target_user')}
          disabled={ending}
        >
          {ending ? 'Ending…' : 'End impersonation'}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
